import { Box, Heading, Icon, Modal, Paragraph, PropertyCard, Text, TextButton, Upsell } from '@zillow/constellation-10';
import {
  IconDollarSignCircleFilled,
  IconHouseUserFilled,
  IconProfileFilled,
  IconSignatureFilled,
  IconYardSignFilled,
} from '@zillow/constellation-icons';

import { LoadingButton, LoadingParagraph, LoadingText, shimmerStyles } from '@zg-rentals/loading-components';
import { stringHelper } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerAccountViewZrmPropertiesWebApiHandler_added,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getPhotoThumbUrl } from '../../../../utils/flexiblePropertyCardUtils';
import propertyUtils from '../../../../utils/propertyUtils';

import {
  emitAddLeaseEvent,
  emitAddTenantEvent,
  emitInviteToApplyEvent,
  emitListPropertyForRentEvent,
  emitSetUpPaymentsEvent,
} from '../../../../analytics/events/propertyAddedNextSteps';
import { useAuth } from '../../../AuthProvider';

import type { AddPropertyDetailsState } from './AddPropertyDetailsModal';

const UpsellSkeleton = () => (
  <Box
    css={{
      display: 'flex',
      gap: '300',
      padding: '400',
      border: '1px solid token(colors.border.muted)',
      borderRadius: '300',
    }}
  >
    <LoadingText isLoading={true} textStyle="heading-lg" loadingWidth="24px">
      &nbsp;
    </LoadingText>
    <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
      <LoadingText isLoading={true} textStyle="heading-xs" loadingWidth="55%">
        &nbsp;
      </LoadingText>
      <LoadingParagraph isLoading={true} textStyle="body-sm" loadingWidth="90%">
        &nbsp;
      </LoadingParagraph>
      <LoadingButton isLoading={true} loadingWidth="80px">
        &nbsp;
      </LoadingButton>
    </Box>
  </Box>
);

type Props = AddPropertyDetailsState & {
  isOpen: boolean;
  onClose: () => void;
  multiplexBuildingSessionId?: string;
  firstUnitListingAlias?: string;
  onAddTenantClick: (listingAlias: string) => void;
};

const PropertyAddedSuccessStep = ({
  isOpen,
  listingAlias,
  multiplexBuildingSessionId,
  firstUnitListingAlias,
  onClose,
  onAddTenantClick,
}: Props): JSX.Element => {
  const { user } = useAuth?.() ?? {};

  const effectiveListingAlias = firstUnitListingAlias || listingAlias;

  const { data: webAnalyticsData } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    { listingAlias: effectiveListingAlias!, includeLeases: false },
    { enabled: isOpen && Boolean(effectiveListingAlias) },
  );

  const { data: addedData } = useRentalManagerAccountViewZrmPropertiesWebApiHandler_added(
    { listingAlias: effectiveListingAlias!, multiplexBuildingSessionId },
    {
      enabled: isOpen && Boolean(effectiveListingAlias),
      meta: {
        tags: ['property-added'],
        context: { api: 'property-added', details: 'account view property added success actions' },
      },
    },
  );

  const { actions, address, photoKey, propertyType, numUnits } = addedData ?? {};

  const { formattedStreet, formattedCityStateZip } = address
    ? propertyUtils.formatAddress(address)
    : { formattedStreet: undefined, formattedCityStateZip: undefined };
  const formattedAddress = [formattedStreet, formattedCityStateZip].filter(Boolean).join(', ');

  const displayPropertyType = propertyUtils.getPropertyTypeLabel({ type: addedData?.type, propertyType }) ?? '';
  const propertyTypeLine =
    numUnits && numUnits > 1
      ? `${displayPropertyType} • ${numUnits} ${stringHelper.pluralizeWord(numUnits, 'unit', 'units')}`
      : displayPropertyType;

  const makeActionHandler =
    (
      emitFn: (args: { user: NonNullable<typeof user>; currentProperty: NonNullable<typeof webAnalyticsData> }) => void,
      url: string | undefined,
    ) =>
    () => {
      if (user && webAnalyticsData) {
        emitFn({ user, currentProperty: webAnalyticsData });
      }
      if (url) {
        window.location.href = url;
      }
    };

  const handleCreateListingClick = makeActionHandler(emitListPropertyForRentEvent, actions?.listing.url);
  const handleInviteToApplyClick = makeActionHandler(emitInviteToApplyEvent, actions?.applications.url);
  const handleAddLeaseClick = makeActionHandler(emitAddLeaseEvent, actions?.leases.url);
  const handleSetUpPaymentsClick = makeActionHandler(emitSetUpPaymentsEvent, actions?.payments.url);
  const handleAddTenantClick = () => {
    if (user && webAnalyticsData) {
      emitAddTenantEvent({ user, currentProperty: webAnalyticsData });
    }
    onAddTenantClick(effectiveListingAlias ?? '');
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      dividers={false}
      header={<Heading level={2}>Your property was added</Heading>}
      body={
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '800',
            paddingBlockEnd: '400',
            paddingInline: { base: '0', md: '400' },
          }}
        >
          {!addedData ? (
            <Box css={{ display: 'flex', gap: '400', alignItems: 'center' }}>
              <Box
                data-loading={true}
                css={{
                  width: '150px',
                  height: '100px',
                  borderRadius: 'obj.soft',
                  flexShrink: '0',
                  ...shimmerStyles,
                }}
              />
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '100', flex: '1' }}>
                <LoadingText isLoading={true} textStyle="body-lg-bold" loadingWidth="60%">
                  &nbsp;
                </LoadingText>
                <LoadingText isLoading={true} textStyle="body-sm" loadingWidth="120px">
                  &nbsp;
                </LoadingText>
              </Box>
            </Box>
          ) : (
            <Box css={{ display: 'flex', gap: '400', alignItems: 'center' }}>
              <Box
                data-testid="success-property-image"
                css={{
                  width: '150px',
                  height: '100px',
                  borderRadius: 'obj.soft',
                  overflow: 'hidden',
                  flexShrink: '0',
                }}
              >
                <PropertyCard.Photo
                  src={getPhotoThumbUrl(photoKey)}
                  alt={formattedAddress}
                  fallbackImg={<PropertyCard.PhotoFallback />}
                  css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>

              <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
                <Text textStyle="body-lg-bold" css={{ display: 'block' }} data-testid="success-address">
                  {formattedAddress}
                </Text>
                <Text textStyle="body-sm" css={{ display: 'block' }} data-testid="success-property-type">
                  {propertyTypeLine}
                </Text>
              </Box>
            </Box>
          )}

          <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
            <Text textStyle="body-bold">What would you like to do next?</Text>

            {!addedData ? (
              <>
                <UpsellSkeleton />
                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
                    gap: '400',
                  }}
                >
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                </Box>
              </>
            ) : (
              <>
                {actions?.listing.isAllowed && (
                  <Upsell
                    data-testid="success-action-list-property"
                    elevated={false}
                    closeable={false}
                    media={<Icon render={<IconYardSignFilled />} />}
                    header={
                      <Heading level={3} textStyle="heading-xs">
                        {multiplexBuildingSessionId ? 'List property or units for rent' : 'List property for rent'}
                      </Heading>
                    }
                    body={
                      <Paragraph>
                        1 million visitors come to Zillow, Trulia, and HotPads every day — show up where they&apos;re
                        looking.
                      </Paragraph>
                    }
                    action={<TextButton onClick={handleCreateListingClick}>Create listing</TextButton>}
                  />
                )}

                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
                    gap: '400',
                  }}
                >
                  {actions?.tenants.isAllowed && (
                    <Upsell
                      data-testid="success-action-add-tenant"
                      tone="soft"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconHouseUserFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Add a tenant
                        </Heading>
                      }
                      body={
                        <Paragraph>Keep track of your residents, their lease terms, and contact details.</Paragraph>
                      }
                      action={<TextButton onClick={handleAddTenantClick}>Add a tenant</TextButton>}
                    />
                  )}

                  {actions?.applications.isAllowed && (
                    <Upsell
                      data-testid="success-action-invite-to-apply"
                      tone="soft"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconProfileFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Invite to apply
                        </Heading>
                      }
                      body={<Paragraph>Screen renters with Zillow&apos;s hassle-free applications.</Paragraph>}
                      action={<TextButton onClick={handleInviteToApplyClick}>Invite to apply</TextButton>}
                    />
                  )}

                  {actions?.leases.isAllowed && (
                    <Upsell
                      data-testid="success-action-add-lease"
                      tone="soft"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconSignatureFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Add a lease
                        </Heading>
                      }
                      body={<Paragraph>Upload a lease for signing, or just to keep your records organized.</Paragraph>}
                      action={<TextButton onClick={handleAddLeaseClick}>Add a lease</TextButton>}
                    />
                  )}

                  {actions?.payments.isAllowed && (
                    <Upsell
                      data-testid="success-action-set-up-payments"
                      tone="soft"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconDollarSignCircleFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Set up payments
                        </Heading>
                      }
                      body={<Paragraph>Collect rent at no cost to you, and in as few as 2 business days.</Paragraph>}
                      action={<TextButton onClick={handleSetUpPaymentsClick}>Set up payments</TextButton>}
                    />
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      }
    />
  );
};

export default PropertyAddedSuccessStep;
